import { ComponentType, SVGProps } from "react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { Box, BoxProps, Flex, Icon, IconProps } from "@chakra-ui/react";
import {
  Header,
  RowData,
  SortDirection,
  Table as ReactTable,
  flexRender,
} from "@tanstack/react-table";
import TooltipIconButton, {
  TooltipIconButtonProps,
} from "components/Shared/TooltipIconButton";

type SortButtonProps = Omit<TooltipIconButtonProps, "icon" | "label"> & {
  IconComponent: ComponentType<SVGProps<SVGElement>> | ComponentType<IconProps>;
  isSorted?: boolean;
  label: string;
};

function SortButton({
  IconComponent,
  isSorted,
  label,
  ...rest
}: SortButtonProps) {
  return (
    <TooltipIconButton
      icon={<Icon as={IconComponent} boxSize=".75em" />}
      variant="ghost"
      size="xs"
      fontSize="1em"
      pb=".5em"
      pt=".5em"
      h="auto"
      opacity={0.4}
      tooltipProps={{ openDelay: 750, closeOnMouseDown: true }}
      _active={{ opacity: 1, color: "yellowGreen" }}
      _hover={{ opacity: 1, backgroundColor: "rgba(255, 255, 255, .25)" }}
      data-active={isSorted || undefined}
      label={
        isSorted ? (
          <>
            {label}{" "}
            <Box as="b" fontSize=".75em">
              (Active)
            </Box>
          </>
        ) : (
          label
        )
      }
      {...rest}
    />
  );
}

type Props = BoxProps & {
  buttonProps?: Partial<SortButtonProps>;
  isSorted?: false | SortDirection;
  onSortAscending: () => void;
  onSortDescending: () => void;
};

export default function SortControls({
  buttonProps,
  isSorted = false,
  onSortAscending,
  onSortDescending,
  ...rest
}: Props) {
  return (
    <Box
      className="sort-controls"
      display="inline-flex"
      flexFlow="column"
      verticalAlign="middle"
      fontSize=".75em"
      {...rest}
    >
      <SortButton
        IconComponent={TriangleUpIcon}
        label="Sort Ascending"
        pb={0}
        borderBottomRadius={0}
        onClick={onSortAscending}
        isSorted={isSorted === "asc"}
        {...buttonProps}
      />
      <SortButton
        IconComponent={TriangleDownIcon}
        label="Sort Descending"
        pt={0}
        borderTopRadius={0}
        onClick={onSortDescending}
        isSorted={isSorted === "desc"}
        {...buttonProps}
      />
    </Box>
  );
}

export function defaultRenderSortingEnabledHeaderContent<D extends RowData>(
  tableInstance: ReactTable<D>,
  header: Header<D, unknown>,
) {
  const content = flexRender(
    header.column.columnDef.header,
    header.getContext(),
  );
  return (
    <Flex
      className="sorting-enabled-header-wrapper"
      display="inline-flex"
      alignItems="center"
      minW="0"
      maxW="100%"
      minH="1.55em"
    >
      {header.column.getCanSort() ? (
        <>
          <Box
            className="sortable-header-content"
            minW="0"
            lineHeight="normal"
            isTruncated
          >
            {content}
          </Box>
          <SortControls
            isSorted={header.column.getIsSorted()}
            onSortAscending={() => header.column.toggleSorting(false)}
            onSortDescending={() => header.column.toggleSorting(true)}
            mt="-.5em"
            mb="-.5em"
            ml=".4em"
          />
        </>
      ) : (
        content
      )}
    </Flex>
  );
}
